import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { type ActivityEvent, type ActorRef, ApiError } from "../api/types";
import { Button } from "../compose/controls";
import {
  CheckCircle2,
  Clock,
  MessagesSquare,
  UserPlus,
  Users,
} from "../compose/Icon";
import {
  SegmentedControl,
  type SegmentedOption,
} from "@norkron/web-segmented-control";
import { EmptyState, ErrorState, SkeletonRows } from "../compose/states";
import {
  activityMeta,
  activityUnitHref,
  actorLabel,
  hasArticleContext,
  roleChipLabel,
} from "../lib/activity";
import { actorFromLegacy } from "../lib/avatar";
import { formatDate, relativeTime } from "../lib/format";
import { Avatar } from "../components/shared/Avatar";
import "../styles/activity.css";

const PAGE_SIZE = 30;

/** The `?scope=` values the feed narrows on: everything, or only article-unit
 *  events. Mirrors the backend's `scope=articles` filter (degrade-open). */
type Scope = "all" | "articles";

const SCOPE_SEGMENTS: SegmentedOption<Scope>[] = [
  { value: "all", label: "All" },
  { value: "articles", label: "Articles" },
];

/** Parse `?scope=` into the canonical union (anything but `articles` → `all`). */
function parseScope(raw: string | null): Scope {
  return raw === "articles" ? "articles" : "all";
}

/** Parse a positive-integer `?unit=` deep-link param, or null. */
function parseUnit(raw: string | null): number | null {
  if (!raw) return null;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
}

/**
 * Activity `/w/:slug/activity` (component-map "Activity / audit"). A
 * reverse-chronological, keyset-paginated feed of what happened in the
 * workspace — who delivered a document, who was invited, who joined. Member-
 * scoped by the backend (a non-member never reaches here: the AppShell redirects
 * an unknown workspace; a 404 degrades to the access-scoped empty state).
 * Reading-room register: quiet rows, terse "time ago", absolute date on hover.
 *
 * IA article-first sweep: a document event whose doc belongs to an article-unit
 * becomes a LINK into the workbench (`/w/:slug/article/:unitId?show=<role>`) and
 * shows the unit title + resource chip; a "All · Articles" scope pill (URL-synced
 * on `?scope=articles`) narrows the feed, and a `?unit=<id>` deep link (from a
 * per-unit feed) scopes to one unit. Non-document / loose events render as before.
 */
export function Activity() {
  const { slug = "" } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const scope = parseScope(searchParams.get("scope"));
  const unit = parseUnit(searchParams.get("unit"));

  const [items, setItems] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [done, setDone] = useState(false);
  // Guards against a stale first-page fetch painting under a changed :slug /
  // filter — every filter change bumps the ticket so an in-flight page is
  // discarded on arrival.
  const ticket = useRef(0);

  // The server-side query the current filters resolve to. `scope=all` omits the
  // param entirely (unfiltered); `unit` deep-links to one unit's events.
  const filterQuery = useMemo(
    () => ({
      ...(scope === "articles" ? { scope: "articles" as const } : {}),
      ...(unit != null ? { unit } : {}),
    }),
    [scope, unit],
  );

  useEffect(() => {
    const mine = ++ticket.current;
    setItems([]);
    setLoading(true);
    setError(null);
    setDone(false);
    api
      .listActivity(slug, { ...filterQuery, limit: PAGE_SIZE })
      .then((page) => {
        if (mine !== ticket.current) return;
        setItems(page);
        setDone(page.length < PAGE_SIZE);
        setLoading(false);
      })
      .catch((e) => {
        if (mine !== ticket.current) return;
        setError(e as Error);
        setLoading(false);
      });
  }, [slug, filterQuery]);

  const loadMore = useCallback(() => {
    const last = items[items.length - 1];
    if (!last || loadingMore) return;
    const mine = ticket.current;
    setLoadingMore(true);
    api
      .listActivity(slug, { ...filterQuery, before: last.id, limit: PAGE_SIZE })
      .then((page) => {
        if (mine !== ticket.current) return;
        setItems((cur) => [...cur, ...page]);
        setDone(page.length < PAGE_SIZE);
        setLoadingMore(false);
      })
      .catch(() => {
        if (mine !== ticket.current) return;
        setLoadingMore(false);
      });
  }, [slug, items, loadingMore, filterQuery]);

  const setScope = useCallback(
    (next: Scope) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next === "articles") p.set("scope", "articles");
          else p.delete("scope");
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const forbidden = error instanceof ApiError && error.status === 404;

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Activity</h1>
          <p className="page__sub">
            Everything that happened in this workspace, newest first.
          </p>
        </div>
        <SegmentedControl
          ariaLabel="Filter activity by scope"
          value={scope}
          options={SCOPE_SEGMENTS}
          segmentTestIdPrefix="filter"
          onChange={setScope}
        />
      </div>

      {loading ? (
        <div className="feed" data-testid="activity-loading">
          <SkeletonRows rows={5} />
        </div>
      ) : forbidden ? (
        <EmptyState
          icon={<Clock size={30} strokeWidth={1.5} />}
          title="No activity here"
          body="This workspace has no activity you can see."
        />
      ) : error ? (
        <ErrorState body="We couldn't load the activity feed." />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Clock size={30} strokeWidth={1.5} />}
          title={
            scope === "articles"
              ? "No article activity yet"
              : "Nothing has happened yet"
          }
          body={
            scope === "articles"
              ? "When an article unit's brief, research, or article changes, it shows up here."
              : "When a document is delivered, someone is invited, or a person joins, it shows up here."
          }
        />
      ) : (
        <>
          <ol className="feed" data-testid="activity-feed">
            {items.map((ev) => (
              <ActivityRow key={ev.id} ev={ev} slug={slug} />
            ))}
          </ol>
          {!done ? (
            <div className="feed__more">
              <Button
                variant="secondary"
                data-testid="activity-load-more"
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? "Loading…" : "Load older"}
              </Button>
            </div>
          ) : (
            <p className="feed__end" data-testid="activity-end">
              You're all caught up.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function VerbIcon({ kind }: { kind: ReturnType<typeof activityMeta>["kind"] }) {
  const size = 14;
  const sw = 1.5;
  if (kind === "delivered") return <CheckCircle2 size={size} strokeWidth={sw} />;
  if (kind === "invited") return <UserPlus size={size} strokeWidth={sw} />;
  if (kind === "joined") return <Users size={size} strokeWidth={sw} />;
  return <MessagesSquare size={size} strokeWidth={sw} />;
}

/** The unit-context line (title + resource chip) for a unit-attached event.
 *  Rendered inside `feed__meta`; null for a loose/system event. */
function ArticleContext({ ev }: { ev: ActivityEvent }) {
  if (!hasArticleContext(ev)) return null;
  const role = roleChipLabel(ev.resource_role);
  return (
    <>
      <span className="feed__dot" aria-hidden>
        ·
      </span>
      <span className="feed__unit" data-testid={`activity-unit-${ev.id}`}>
        {ev.unit_title}
      </span>
      {role ? (
        <span
          className="role-chip role-chip--on feed__role"
          data-testid={`activity-role-${ev.id}`}
        >
          {role}
        </span>
      ) : null}
    </>
  );
}

/** Resolve the feed event's avatar actor: prefer the server `actor` ref; else a
 *  system ref (null actor_name) or a legacy user ref from the display label. */
function feedActor(ev: ActivityEvent, isSystem: boolean, label: string): ActorRef {
  if (ev.actor) return ev.actor;
  if (isSystem) return { id: 0, type: "system", name: "System", avatar_url: null };
  return actorFromLegacy(label);
}

function ActivityRow({ ev, slug }: { ev: ActivityEvent; slug: string }) {
  const meta = activityMeta(ev.verb);
  const actor = actorLabel(ev);
  const isSystem = ev.actor_name == null;
  const href = activityUnitHref(slug, ev);

  const body = (
    <div className="feed__body">
      <div className="feed__summary">
        <span
          className={`feed__verb feed__verb--${meta.kind}`}
          data-testid={`activity-verb-${ev.id}`}
        >
          <VerbIcon kind={meta.kind} />
          <span className="feed__verb-label">{meta.label}</span>
        </span>
        <span className="feed__text">{ev.summary}</span>
      </div>
      <div className="feed__meta">
        <span className="feed__actor">{actor}</span>
        <span className="feed__dot" aria-hidden>
          ·
        </span>
        <time
          className="feed__time"
          dateTime={ev.created_at}
          title={formatDate(ev.created_at)}
        >
          {relativeTime(ev.created_at)}
        </time>
        <ArticleContext ev={ev} />
      </div>
    </div>
  );

  return (
    <li className="feed__item" data-testid={`activity-${ev.id}`}>
      <div className="feed__rail">
        <Avatar actor={feedActor(ev, isSystem, actor)} size="md" className="feed__avatar" />
        <span className="feed__line" aria-hidden />
      </div>
      {href ? (
        <Link
          className="feed__link"
          to={href}
          data-testid={`activity-link-${ev.id}`}
        >
          {body}
        </Link>
      ) : (
        body
      )}
    </li>
  );
}
